import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useCompany } from "@/lib/company";
import { fmtDate, kes } from "@/lib/money";
import { Page, Btn, Table, Tr, Td, Pill, Loading } from "@/components/kit";
import { download } from "@/lib/download";

export default function Settlements() {
  const { company } = useCompany();
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const file = useRef(null);
  // the settled list: one day's payments from the bank (the latest by default) or a period
  const [view, setView] = useState({ mode: "day", day: "", from: "", to: "" });
  const load = useCallback(async () => {
    if (company) setData((await api.get(`/companies/${company.id}/settlements`)).data);
  }, [company]);
  useEffect(() => { load().catch(() => setData({ settled: [], unsettled: [], unsettled_total: 0, visa_balance: 0 })); }, [load]);
  const confirm = async (uid, confirmed) => {
    try {
      await api.post(`/companies/${company.id}/settlements/confirm`, { uid, confirmed });
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };
  const upload = async (f) => {
    if (!f) return;
    setBusy(true);
    try {
      const form = new FormData();
      form.append("file", f);
      const r = (await api.post(`/companies/${company.id}/settlements/upload`, form)).data;
      const n = r.posted.reduce((a, p) => a + p.count, 0);
      toast.success(n ? `${n} card sales posted to KCB${r.already_in ? ` (${r.already_in} were already in)` : ""}` : `All ${r.already_in} were already in`);
      Object.entries(r.station_settlements || {}).forEach(([station, s]) => (s.error
        ? toast.error(`${station}: card settlements not recorded - upload the report again (${s.error})`)
        : toast.success(`${station}: ${s.added} card settlement${s.added === 1 ? "" : "s"} recorded${s.already_in ? `, ${s.already_in} already there` : ""}`)));
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setBusy(false);
      if (file.current) file.current.value = "";
    }
  };
  if (!data) return <Page title="Card settlements"><Loading /></Page>;
  return (
    <Page title="Card settlements" actions={<>
      <input ref={file} type="file" accept=".csv,text/csv" className="hidden" onChange={(e) => upload(e.target.files?.[0])} />
      <Btn onClick={() => download(`/companies/${company.id}/settlements/excel`)}>Download Excel</Btn>
      <Btn kind="primary" disabled={busy} onClick={() => file.current?.click()}>{busy ? "Uploading…" : "Upload KCB statement (CSV)"}</Btn>
    </>}>
      <p className="mt-2 max-w-3xl text-sm text-[#5F5C58]">
        The KCB merchant report (CsvReport_….csv). Each settled sale moves from "Visa POS – due from KCB" into KCB, with any
        commission to bank charges, and is matched to the card sale on the shift by its auth code. Uploading a file twice is safe.
      </p>
      <div className="mt-4 grid gap-3" style={{ gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))" }}>
        <section className="rounded-[10px] border border-[#E4E2DE] bg-white p-4">
          <div className="text-xs font-semibold uppercase text-[#5F5C58]">Due from KCB</div>
          <div className="mt-1 font-mono text-2xl tabular-nums">{kes(data.visa_balance)}</div>
          <div className="text-xs text-[#5F5C58]">Card sales not yet paid by the bank</div>
        </section>
        <section className="rounded-[10px] border border-[#E4E2DE] bg-white p-4">
          <div className="text-xs font-semibold uppercase text-[#5F5C58]">Waiting on the shifts</div>
          <div className="mt-1 font-mono text-2xl tabular-nums">{kes(data.unsettled_total)}</div>
          <div className="text-xs text-[#5F5C58]">{data.unsettled.length} card receipts not on a statement yet</div>
        </section>
        <section className={`rounded-[10px] border p-4 ${data.not_on_shift ? "border-[#E8B4B4] bg-[#FBEFEF]" : "border-[#E4E2DE] bg-white"}`}>
          <div className="text-xs font-semibold uppercase text-[#5F5C58]">Paid, but not on a shift</div>
          <div className={`mt-1 font-mono text-2xl tabular-nums ${data.not_on_shift ? "text-[#A11D1D]" : ""}`}>{kes(data.not_on_shift_total || 0)}</div>
          <div className="text-xs text-[#5F5C58]">{data.not_on_shift ? `${data.not_on_shift} bank payments with no card sale on a shift - check the receipts were itemized` : "Every bank payment matches a shift receipt"}</div>
        </section>
      </div>

      {data.differs > 0 && (
        <>
          <h2 className="mt-8 text-[15px] font-semibold">Matched, but the amount differs</h2>
          <p className="mt-1 max-w-3xl text-sm text-[#5F5C58]">
            The auth code is the same, so this is that card sale, but the shift has a different amount (cents dropped, or two receipts swapped).
            The books follow the bank. Correct the receipt on the shift, then confirm. Net difference {kes(data.differs_net)}.
          </p>
          <Table minWidth={760} cols={[{ label: "Paid" }, { label: "Auth" }, { label: "Shift" }, { label: "Shift amount", align: "right" },
            { label: "Bank amount", align: "right" }, { label: "Difference", align: "right" }, { label: "" }]}>
            {data.settled.filter((s) => s.shift && Math.abs(s.diff) >= 0.005).map((s) => (
              <Tr key={s.uid}>
                <Td>{fmtDate(s.post_date)}</Td><Td>{s.auth}</Td>
                <Td>{s.shift}<span className="block text-xs text-[#5F5C58]">{s.receipt}</span></Td>
                <Td right>{kes(s.shift_amount)}</Td><Td right>{kes(s.amount)}</Td>
                <Td right><b className={s.confirmed ? "text-[#5F5C58]" : "text-[#A11D1D]"}>{s.diff > 0 ? "+" : ""}{kes(s.diff)}</b></Td>
                <Td right>{s.confirmed
                  ? <button type="button" className="text-xs text-[#5F5C58] underline" onClick={() => confirm(s.uid, false)}>Confirmed - undo</button>
                  : <Btn onClick={() => confirm(s.uid, true)}>Confirm</Btn>}</Td>
              </Tr>
            ))}
          </Table>
        </>
      )}

      <h2 className="mt-8 text-[15px] font-semibold">Not settled yet</h2>
      {data.unsettled.length === 0 ? <p className="py-4 text-sm text-[#5F5C58]">Every card sale on the shifts has been paid</p> : (
        <Table minWidth={620} cols={[{ label: "Shift" }, { label: "Receipt" }, { label: "Method" }, { label: "Amount", align: "right" }]}>
          {data.unsettled.map((s) => (
            <Tr key={s.id}><Td>{s.shift}</Td><Td>{s.reference}</Td><Td className="text-[#5F5C58]">{s.method}</Td><Td right>{kes(s.amount)}</Td></Tr>
          ))}
        </Table>
      )}

      <h2 className="mt-8 text-[15px] font-semibold">Settled</h2>
      <SettledFilter settled={data.settled} view={view} setView={setView} />
      {data.settled.length === 0 ? <p className="py-4 text-sm text-[#5F5C58]">No statement uploaded yet</p> : (
        <Table minWidth={820} cols={[{ label: "Sale" }, { label: "Paid" }, { label: "Card" }, { label: "Auth" }, { label: "Shift" },
          { label: "Amount", align: "right" }, { label: "Commission", align: "right" }, { label: "Into KCB", align: "right" }]}>
          {settledIn(data.settled, view).map((s, i) => (
            <Tr key={i}>
              <Td>{fmtDate(s.txn_date)}</Td><Td>{fmtDate(s.post_date)}</Td><Td className="text-[#5F5C58]">{s.card}</Td><Td>{s.auth}</Td>
              <Td>{s.shift
                ? <span>{s.shift}<span className="block text-xs text-[#5F5C58]">{s.receipt}</span>{Math.abs(s.diff) >= 0.005 && <Pill kind="amber">amount differs by {kes(Math.abs(s.diff))}</Pill>}</span>
                : <span><Pill kind="amber">Not on a shift</Pill>{s.suggest && <span className="block text-xs text-[#5F5C58]">Maybe {s.suggest.shift} receipt {s.suggest.receipt} ({kes(s.suggest.amount)}), code differs</span>}</span>}</Td>
              <Td right>{kes(s.amount)}</Td><Td right>{s.commission ? kes(s.commission) : "–"}</Td><Td right>{kes(s.net)}</Td>
            </Tr>
          ))}
        </Table>
      )}
    </Page>
  );
}

const paidDays = (settled) => [...new Set(settled.map((s) => s.post_date))].sort().reverse();

function settledIn(settled, view) {
  const day = view.day || paidDays(settled)[0];
  return settled.filter((s) => (view.mode === "day" ? s.post_date === day
    : (!view.from || s.post_date >= view.from) && (!view.to || s.post_date <= view.to)));
}

function SettledFilter({ settled, view, setView }) {
  if (!settled.length) return null;
  const days = paidDays(settled);
  const shown = settledIn(settled, view);
  const sum = (k) => shown.reduce((a, s) => a + (Number(s[k]) || 0), 0);
  const btn = (mode, label) => (
    <button type="button" onClick={() => setView({ ...view, mode })}
      className={`px-3 py-1.5 text-sm ${view.mode === mode ? "bg-brand text-white" : "bg-white text-[#2B2926]"}`}>{label}</button>
  );
  return (
    <div className="mt-2 flex flex-wrap items-center gap-3">
      <div className="flex overflow-hidden rounded-md border border-[#E4E2DE]">{btn("day", "One day")}{btn("period", "Period")}</div>
      {view.mode === "day" ? (
        <select id="settled-day" value={view.day || days[0]} onChange={(e) => setView({ ...view, day: e.target.value })}
          className="h-9 rounded-md border border-[#E4E2DE] bg-white px-2 text-sm">
          {days.map((d) => <option key={d} value={d}>Paid {fmtDate(d)}</option>)}
        </select>
      ) : (
        <span className="flex items-center gap-2 text-sm">
          <input id="settled-from" type="date" value={view.from} onChange={(e) => setView({ ...view, from: e.target.value })} className="h-9 rounded-md border border-[#E4E2DE] px-2" />
          to
          <input id="settled-to" type="date" value={view.to} onChange={(e) => setView({ ...view, to: e.target.value })} className="h-9 rounded-md border border-[#E4E2DE] px-2" />
        </span>
      )}
      <span className="text-sm text-[#5F5C58]">
        {shown.length} sales · {kes(sum("amount"))}{sum("commission") ? ` · commission ${kes(sum("commission"))}` : ""} · into the bank <b className="text-[#2B2926]">{kes(sum("net"))}</b>
      </span>
    </div>
  );
}
